// "What hasn't been dealt with?" Greg's replies pill.
//
// ⚠️ IT TRACKS OUTCOMES, NOT WHETHER SOMEBODY READ IT. Greg, 2026-09-21: "I don't necessarily
// need to know have they read it or not. I need to know has it been dealt with or not. Is it a
// pending request that hasn't been resolved... if they've got three that they've read that have
// said yes and they haven't done them yet, I'd want to know that."
//
// An earlier cut filtered on Outlook's isRead flag in the rep's mailbox. It was useless twice
// over: the reps read their own mail within the hour so it was permanently empty, and 35 of 45
// stored Graph ids no longer resolved because they file mail into folders and a Graph id changes
// when a message moves. See lib/outreach-reply-status.js for what replaced it.
//
// ⚠️ THE ROW SHOWS WHAT THE CUSTOMER WROTE, NOT THE RAW PREVIEW. Graph's bodyPreview carries our
// own invite underneath their reply, so every single one contains "VIP Club" - because we wrote
// it. See lib/outreach-reply-read.js.
//
// ⚠️ THE BODY IS FETCHED WHEN HE OPENS A ROW, AND NEVER STORED. The reply scanner deliberately
// keeps only a 255-character preview; we are not warehousing the text of customers' email.
import React, { useState, useEffect, useCallback } from 'react';
import { Loader, ChevronDown, ChevronRight, Check, RefreshCw } from 'lucide-react';
import { getAccessToken, getCurrentUser } from '../services/auth';
import { DncModal, DncList } from './DoNotContact';
import { neonQuery } from '../services/api';

const NAVY = 'var(--navy, #00395C)';
const ORANGE = 'var(--orange, #DC582A)';
const GREEN = '#2f855a';

async function call(body) {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const headers = { 'Content-Type': 'application/json' };
  const t = getAccessToken();
  if (t) headers.Authorization = 'Bearer ' + t;
  const res = await fetch(base + '/outreach-replies-board', {
    method: 'POST', headers, body: JSON.stringify(body),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error || 'replies ' + res.status);
  return j;
}

// The three kinds of outstanding work, in the order they cost us money. A yes that never got set
// up is somebody who asked to pay us and did not get the chance.
const WORK = [
  { key: 'to_set_up', label: 'Said yes, not set up', tone: ORANGE },
  { key: 'to_answer', label: 'Asked, no reply yet', tone: '#975a16' },
  { key: 'to_read', label: 'Needs a look', tone: 'var(--gray-600)' },
];
const STATE_LABEL = {
  to_set_up: 'Set them up', to_answer: 'Needs a reply', to_read: 'Needs a look',
  joined: 'Joined', answered: 'Answered', closed: 'Said no', auto: 'Out of office',
  done: 'Done', unknown: 'Could not check', fresh: 'New today',
  thanks: 'Thank-you', stale: 'Old', unrelated: 'Not about the offer',
  to_remove: 'Asked to be removed', removed: 'Do not contact',
};
const STATE_TONE = {
  to_set_up: ORANGE, to_answer: '#975a16', to_read: 'var(--gray-600)',
  joined: GREEN, answered: GREEN, closed: 'var(--gray-400)', auto: 'var(--gray-400)',
  done: GREEN, unknown: '#c53030', fresh: NAVY,
  thanks: 'var(--gray-400)', stale: 'var(--gray-400)', unrelated: 'var(--gray-400)',
  to_remove: '#b42318', removed: 'var(--gray-400)',
};

const esc = (s) => String(s == null ? '' : s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Scan Now. The sweep reads three mailboxes and takes about a minute, far past the ~26-second
// ceiling on an ordinary function, so it runs in the background twin; a signed-in person is never
// throttled there. The 202 it answers proves nothing - the button then watches the scan ledger
// for a run newer than the one on screen, which is the only real evidence the sweep happened.
async function startScan() {
  const base = window.location.hostname === 'localhost'
    ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
  const headers = { 'Content-Type': 'application/json' };
  const t = getAccessToken();
  if (t) headers.Authorization = 'Bearer ' + t;
  await fetch(base + '/outreach-reply-scan-background', {
    method: 'POST', headers, body: JSON.stringify({ trigger: 'button' }),
  });
}

// initialData is for scripts/test-render-panels.js, which renders on the server where the load
// effect never runs - without it only the loading spinner could ever be tested.
export default function OutreachRepliesPanel({ initialData = null } = {}) {
  const [data, setData] = useState(initialData);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(!initialData);
  const [filter, setFilter] = useState(null);
  // Opens on the work, not the archive.
  const [openOnly, setOpenOnly] = useState(true);
  const [open, setOpen] = useState(null);
  const [bodies, setBodies] = useState({});
  const [days, setDays] = useState(30);
  const [scan, setScan] = useState(null); // null | { msg, busy, bad }

  const [dncFor, setDncFor] = useState(null);
  const [dncTick, setDncTick] = useState(0);
  const load = useCallback(async (d) => {
    setLoading(true);
    try { setData(await call({ action: 'list', days: d })); setErr(null); }
    catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (!initialData) load(days); }, [load, days, initialData]);

  const scanNow = async () => {
    const before = Number((data && data.lastScan && data.lastScan.id) || 0);
    setScan({ busy: true, msg: 'Checking Ashley, Linda and Rachael\'s mailboxes. This takes about a minute.' });
    try {
      await startScan();
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        const j = await call({ action: 'lastscan' });
        const ls = j && j.lastScan;
        if (ls && Number(ls.id) > before && (ls.finished || ls.died)) {
          await load(days);
          if (ls.died) { setScan({ bad: true, msg: 'The scan stopped before it finished. Try again in a few minutes.' }); return; }
          setScan({
            bad: ls.failures > 0,
            msg: (ls.recorded ? ls.recorded + ' new ' + (ls.recorded === 1 ? 'reply' : 'replies') + ' found.' : 'Nothing new since the last check.')
              + (ls.failures > 0 ? ' One or more mailboxes could not be read this time, so this may be incomplete.' : ''),
          });
          return;
        }
      }
      setScan({ bad: true, msg: 'The scan did not report back within five minutes. The hourly check will still run.' });
    } catch (e) {
      setScan({ bad: true, msg: 'Could not start the scan: ' + e.message });
    }
  };

  const openRow = async (r) => {
    if (open === r.id) { setOpen(null); return; }
    setOpen(r.id);
    if (bodies[r.id]) return;
    setBodies((b) => Object.assign({}, b, { [r.id]: { loading: true } }));
    try {
      const j = await call({ action: 'body', id: r.id });
      setBodies((b) => Object.assign({}, b, {
        [r.id]: { html: j.body, contentType: j.contentType, error: j.error },
      }));
    } catch (e) {
      setBodies((b) => Object.assign({}, b, { [r.id]: { error: e.message } }));
    }
  };

  // ⚠️ THE TICK IS AN OVERRIDE, NOT THE MECHANISM. Most rows resolve themselves - a yes closes
  // when they become a member, a question closes when the rep writes back - and this is only for
  // the ones nothing can decide. It marks locally and leaves the row where it is; re-filtering
  // under his cursor is the one thing a work list must not do.
  const markDone = (r, e, note) => {
    if (e) e.stopPropagation();
    setData((d) => (d ? Object.assign({}, d, {
      replies: d.replies.map((x) => (x.id === r.id
        ? Object.assign({}, x, { outstanding: false, state: 'done', why: note ? 'closed: ' + note : 'you marked it done', justDone: true })
        : x)),
    }) : d));
    call({ action: 'mark', id: r.id, note: note || undefined }).catch(() => {});
  };

  // A yes closes itself when they become a member. This is the other honest ending - tried and
  // could not finish - and it has to say why, or it is a hidden gap. Same tick as the Today's
  // Goal waiting list. Cancelling closes nothing.
  const couldNotSetUp = (r, e) => {
    if (e) e.stopPropagation();
    const note = window.prompt(
      'Close ' + r.customer + ' as could not sign up?\n\nUse this when you have tried and could not get them set up. What happened?',
      'Card declined. Emailed and called, no response.');
    if (note === null || !note.trim()) return;
    markDone(r, null, note.trim());
  };

  if (loading && !data) {
    return <div style={{ padding: 24, color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading replies...</div>;
  }
  if (err) return <div style={{ padding: 16, color: '#c53030' }}>{err}</div>;
  // A colleague without access sees nothing at all rather than an error.
  if (data && data.allowed === false) return null;

  const s = (data && data.summary) || {};
  const inView = ((data && data.replies) || [])
    .filter((r) => !openOnly || r.outstanding || r.justDone || r.state === 'fresh');
  const rows = inView.filter((r) => !filter || r.state === filter);
  // The cards count what is on screen, so they always add up to the number above them.
  const counts = inView.reduce((a, r) => {
    a[r.state] = (a[r.state] || 0) + 1;
    return a;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: openOnly ? ORANGE : NAVY }}>
          {openOnly ? (s.outstanding || 0) : (s.total || 0)}
        </div>
        <div style={{ fontSize: 13, color: 'var(--gray-600)' }}>
          {openOnly
            ? 'replies still waiting on somebody'
            : <>replies in the last {data && data.days} days</>}
          {s.joined > 0 && <> &middot; <b style={{ color: GREEN }}>{s.joined} of them are members now</b></>}
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => { setOpenOnly(!openOnly); setFilter(null); }}
          style={{
            border: '1px solid ' + (openOnly ? ORANGE : 'var(--gray-300)'),
            background: openOnly ? ORANGE : '#fff',
            color: openOnly ? '#fff' : 'var(--gray-600)',
            borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >{openOnly ? 'Not dealt with' : 'Showing everything'}</button>
        {[30, 90].map((d) => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              border: '1px solid ' + (days === d ? NAVY : 'var(--gray-300)'),
              background: days === d ? NAVY : '#fff',
              color: days === d ? '#fff' : 'var(--gray-600)',
              borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >{d} days</button>
        ))}
      </div>

      {/* ⚠️ HOW FRESH THIS IS, AND WHAT CAME IN TODAY INCLUDING WHAT IS HANDLED. Without this line a
          day where the reps answered everything looked exactly like a scanner that had stopped. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12, fontSize: 12, color: 'var(--gray-600)' }}>
        <span>
          {data && data.lastScan
            ? (data.lastScan.running
              ? <>A check is running now (started {data.lastScan.started}).</>
              : data.lastScan.died
                ? <b style={{ color: '#c53030' }}>The last check stopped before it finished.</b>
                : <>Last checked {data.lastScan.finished}{data.lastScan.failures > 0
                  && <b style={{ color: '#c53030' }}> (a mailbox could not be read)</b>}.</>)
            : 'Not checked yet.'}
          {s.today && s.today.total > 0 && (
            <> {' '}&middot; {s.today.total} came in today: {s.today.dealtWith} dealt with
              {s.today.fresh > 0 && <>, <b style={{ color: NAVY }}>{s.today.fresh} new</b></>}
              {s.today.outstanding > 0 && <>, {s.today.outstanding} waiting</>}</>
          )}
          {s.today && s.today.total === 0 && <> &middot; nothing has come in today yet</>}
        </span>
        <button
          onClick={scanNow}
          disabled={!!(scan && scan.busy)}
          style={{
            border: '1px solid ' + NAVY, background: '#fff', color: NAVY,
            borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700,
            cursor: scan && scan.busy ? 'wait' : 'pointer', fontFamily: 'inherit',
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}
        >
          {scan && scan.busy ? <Loader size={12} className="spin" /> : <RefreshCw size={12} />}
          {scan && scan.busy ? 'Scanning...' : 'Scan Now'}
        </button>
        {scan && scan.msg && (
          <span style={{ color: scan.bad ? '#c53030' : scan.busy ? 'var(--gray-600)' : GREEN, fontWeight: scan.busy ? 400 : 600 }}>
            {scan.msg}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {WORK.map((b) => (
          <button
            key={b.key}
            onClick={() => setFilter(filter === b.key ? null : b.key)}
            style={{
              flex: '1 1 160px', minWidth: 150, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
              border: '1px solid ' + (filter === b.key ? b.tone : 'var(--gray-200)'),
              background: filter === b.key ? 'rgba(0,57,92,0.04)' : '#fff',
              borderRadius: 8, padding: '9px 12px',
            }}
          >
            <div style={{ fontSize: 21, fontWeight: 800, color: b.tone }}>{counts[b.key] || 0}</div>
            <div style={{ fontSize: 11.5, color: 'var(--gray-600)' }}>{b.label}</div>
          </button>
        ))}
      </div>

      {filter && (
        <div style={{ fontSize: 12, marginBottom: 8, color: 'var(--gray-600)' }}>
          Showing {rows.length}.{' '}
          <button
            onClick={() => setFilter(null)}
            style={{ border: 'none', background: 'none', color: ORANGE, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 12 }}
          >Show all</button>
        </div>
      )}

      <div style={{ border: '1px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        {rows.length === 0 && (
          <div style={{ padding: 20, color: 'var(--gray-500)', fontSize: 13 }}>
            {openOnly ? (
              <>Nothing waiting on anybody.{' '}
                <button
                  onClick={() => setOpenOnly(false)}
                  style={{ border: 'none', background: 'none', color: ORANGE, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 13 }}
                >Show everything</button>
              </>
            ) : 'Nothing here.'}
          </div>
        )}
        {rows.map((r) => {
          const isOpen = open === r.id;
          const body = bodies[r.id];
          const tone = STATE_TONE[r.state] || 'var(--gray-600)';
          const plain = '<pre style="white-space:pre-wrap;font:13px/1.5 system-ui,sans-serif;margin:0">'
            + esc(body && body.html) + '</pre>';
          return (
            <div key={r.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
              <div
                onClick={() => openRow(r)}
                style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 12px',
                  cursor: 'pointer', background: isOpen ? 'rgba(0,57,92,0.03)' : '#fff',
                }}
              >
                <div style={{ paddingTop: 2, color: 'var(--gray-400)' }}>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </div>
                <div style={{ width: 92, flexShrink: 0, fontSize: 11.5, color: 'var(--gray-500)' }}>
                  {String(r.received || '').slice(5)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: r.outstanding ? 700 : 500, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.outstanding && <span style={{ color: tone, marginRight: 5 }}>&bull;</span>}
                    {r.customer}
                    {r.isMember && (
                      <span style={{ color: GREEN, fontWeight: 700, fontSize: 11, marginLeft: 6 }}>
                        <Check size={11} /> member
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--gray-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.said || r.subject}
                  </div>
                </div>
                <div style={{ width: 80, flexShrink: 0, textAlign: 'right', fontSize: 11, color: 'var(--gray-500)' }}>
                  {r.rep}
                  {r.mode ? (
                    <div style={{ color: 'var(--gray-400)' }}>{r.mode === 'winback' ? 'win-back' : r.mode === 'winback_followup' ? 'win-back follow-up' : r.mode}</div>
                  ) : null}
                </div>
                <div style={{ width: 112, flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: tone }}>
                    {STATE_LABEL[r.state] || r.state}
                  </div>
                  {/* Only offered where nothing can close it on its own. A yes closes when they
                      become a member, and ticking that by hand would hide a real gap. */}
                  {r.state === 'to_remove' && r.customerId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setDncFor(r); }}
                      style={{ border: '1px solid #b42318', background: '#fff', color: '#b42318', fontSize: 10.5, fontWeight: 700, borderRadius: 5, cursor: 'pointer', padding: '2px 7px', fontFamily: 'inherit', marginTop: 2 }}
                    >Do not contact</button>
                  )}
                  {r.outstanding && r.state !== 'to_set_up' && r.state !== 'to_remove' && (
                    <button
                      onClick={(e) => markDone(r, e)}
                      style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 10.5, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                    >mark done</button>
                  )}
                  {r.outstanding && r.state === 'to_set_up' && (
                    <button
                      onClick={(e) => couldNotSetUp(r, e)}
                      style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 10.5, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}
                    >could not sign up</button>
                  )}
                </div>
              </div>

              {isOpen && (
                <div style={{ padding: '4px 12px 14px 126px', fontSize: 13 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--gray-500)', marginBottom: 6 }}>
                    {r.subject}
                    {r.invitedOn ? ' · invited ' + r.invitedOn : ''}
                    {r.why ? ' · ' + r.why : ''}
                    {r.customerId && !r.isDnc && r.state !== 'to_remove' && (
                      <button
                        onClick={() => setDncFor(r)}
                        style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', marginLeft: 10, padding: 0, fontFamily: 'inherit' }}
                      >Do not contact this customer</button>
                    )}
                    {r.customerId && (
                      <button
                        onClick={async () => {
                          // ⚠️ WRITES TO PRODUCTION dbo.Chargebacks (lib/chargebacks.js), which needs the
                          // disputed AMOUNT; the order number (as printed - the BC order #) ties it to the order. No
                          // default amount - an order dispute can be any size, so a guess would be a wrong record.
                          // Saved as Crab Place / Rebutted; the status and category are changed on the Chargebacks page.
                          const orderIn = window.prompt('Mark ' + r.customer + ' as a chargeback on an order? They will not be offered the VIP Club.\n\nOrder # (optional):', '');
                          if (orderIn === null) return;
                          const orderId = String(orderIn).replace(/\D/g, '');
                          const amountIn = window.prompt('Disputed amount for ' + (orderId ? 'order ' + orderId : 'the order') + ' (e.g. 292.89):', '');
                          if (amountIn === null) return;
                          const amount = Number(String(amountIn).replace(/[^0-9.]/g, ''));
                          if (!Number.isFinite(amount) || amount <= 0) { window.alert('Enter the disputed amount, e.g. 292.89. Nothing was recorded.'); return; }
                          const note = window.prompt('Any note? (optional)', '');
                          if (note === null) return;
                          const u = getCurrentUser() || {};
                          try {
                            const res = await neonQuery('vip_chargeback_flag', { customerId: r.customerId, email: r.customer, kind: 'order', orderNumber: orderId || null, amount, note, adminId: u.id, repName: u.staffName });
                            if (res && res.flagged === false) { window.alert(res.reason || 'The chargeback was not recorded.'); return; }
                          } catch (e) { window.alert('The chargeback was not recorded: ' + e.message); return; }
                          markDone(r, { stopPropagation() {} });
                        }}
                        style={{ border: 'none', background: 'none', color: 'var(--gray-500)', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', marginLeft: 10, padding: 0, fontFamily: 'inherit' }}
                      >Mark chargeback</button>
                    )}
                  </div>
                  {!body || body.loading ? (
                    <span style={{ color: 'var(--gray-500)' }}><Loader size={12} className="spin" /> opening...</span>
                  ) : body.error ? (
                    <span style={{ color: '#c53030' }}>{body.error}</span>
                  ) : (
                    // ⚠️ SANDBOXED IFRAME, NEVER innerHTML. This is HTML a stranger emailed us, and
                    // rendering it into the page would run whatever it carries. srcDoc with an empty
                    // sandbox (no allow-scripts) means it can only ever be looked at.
                    <iframe
                      title="reply"
                      sandbox=""
                      srcDoc={body.contentType === 'html' ? body.html : plain}
                      style={{ width: '100%', height: 260, border: '1px solid var(--gray-200)', borderRadius: 6, background: '#fff' }}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <DncList refreshKey={dncTick} />
      {dncFor && (
        <DncModal
          customer={{ customerId: dncFor.customerId, email: dncFor.customer }}
          source="replies"
          defaultReason={/pass|deceased|died/i.test(dncFor.said || '') ? 'passed_away' : 'asked_to_stop'}
          onClose={() => setDncFor(null)}
          onDone={() => { setDncFor(null); setDncTick((n) => n + 1); load(days); }}
        />
      )}
    </div>
  );
}
